import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
// MUI
import { Grid, Skeleton, Stack, Typography } from "@mui/material";

// Third Party
import { v4 as uuidv4 } from "uuid";

// Hooks
import { useLocationId } from "@Common/hooks/Location/useLocationId";
// Contexts
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// APIs
import { getInventoryStatusList } from "@/Settings/api/AdministrationTools/queries";

// Components
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import InventoryMappingTable from "../InventoryMappingTable";

// Constants
import { ON_UPDATE_REYNOLDS_INVENTORY_MAPPING } from "@Settings/api/AdministrationTools/subscriptions";

// Types
import { GraphqlSubscriptionResponse } from "@Common/api/types/GraphqlSubscriptionResponse";
import { IDMSStatusType, IStatusesList } from "@Settings/types/administrationTools/dealerManagementSystem";
import { IAdministrationTools, IIntegration, IReynoldsInventoryMapping } from "@Settings/types/administrationTools/model";

type InventoryMappingProps = {
	integrations: IIntegration;
};

export default function InventoryMapping(props: InventoryMappingProps) {
	const queryClient = useQueryClient();
	const { addSubscription, getSubscription } = useSubscriptions();

	const locationId = useLocationId();

	const inventorySettingsData = props.integrations?.reynoldsInventoryMapping;
	const dmsProvider = props.integrations?.dmsProvider?.provider; // Example: "R&R"

	/********************************************* QUERIES ************************************************/
	const { data: baseDataList } = useQuery({
		queryKey: ["inventoryMapping", locationId, dmsProvider],
		queryFn: () => getInventoryStatusList(dmsProvider),
		staleTime: Infinity,
		enabled: Boolean(locationId) && Boolean(dmsProvider),
	});

	const { data, isLoading } = useQuery({
		queryKey: ["inventoryMergedData", locationId],
		queryFn: async () => {
			const mergeConfigWithBaseData = (baseDataList: IStatusesList[], inventorySettingsData: IReynoldsInventoryMapping) => {
				return baseDataList.map((baseData) => {
					const title = baseData.title;
					const settingsData = inventorySettingsData[title];

					const mergedStatuses = baseData.statuses.map((status) => {
						const statusConfig = settingsData.find((config) => config.name === status.status);
						if (statusConfig) {
							return { ...status, ...statusConfig, _id: uuidv4() };
						} else {
							return { ...status, _id: uuidv4() };
						}
					});

					settingsData.forEach((config) => {
						if (!mergedStatuses.find((status) => status.status === config.name)) {
							mergedStatuses.push({ _id: uuidv4(), status: config.name, ...config });
						}
					});

					return { ...baseData, statuses: mergedStatuses };
				});
			};

			const newList = mergeConfigWithBaseData(baseDataList?.statusesList ?? [], inventorySettingsData);

			const result = newList.map((item) => {
				const sortedStatuses = item.statuses.sort((a, b) => {
					if (a.isCustom && !b.isCustom) {
						return 1;
					} else if (!a.isCustom && b.isCustom) {
						return -1;
					} else {
						return 0;
					}
				});

				return { ...item, statuses: sortedStatuses };
			});

			return result;
		},
		staleTime: Infinity,
		enabled: !!locationId && !!baseDataList && !!inventorySettingsData,
	});

	/********************************************* SUBSCRIPTIONS ************************************************/
	const updateReynoldsSubscription = getSubscription(["updateReynoldsInventoryMappingStatuses", locationId]);
	updateReynoldsSubscription?.subscriptions?.onUpdateReynoldsInventoryMapping?.setCallback(
		(result: GraphqlSubscriptionResponse<"onUpdateReynoldsInventoryMapping", IAdministrationTools>) => {
			const updatedReynoldsMapping = result?.data?.onUpdateReynoldsInventoryMapping?.integrations?.reynoldsInventoryMapping;
			if (!updatedReynoldsMapping) {
				return;
			}
			queryClient.setQueryData(["inventoryMergedData", locationId], (oldData: IStatusesList[]) => {
				let updateDataCopy = [...(oldData || [])];
				for (let oldDataItem of updateDataCopy) {
					const title = oldDataItem.title;
					const newSection = title && updatedReynoldsMapping[title];
					if (newSection) {
						let oldDataSection = oldDataItem?.statuses;
						const result: IDMSStatusType[] = [];
						oldDataSection.forEach((item) => {
							const foundItem = newSection.find((newItem) => newItem.name === item.name);
							if (foundItem) {
								result.push({ _id: item._id, status: foundItem.name, ...foundItem });
							}
						});

						oldDataItem.statuses = result;
						const newItems = newSection.filter((newItem) => {
							return !oldDataItem.statuses.some((item) => item.name === newItem.name);
						});

						newItems.forEach((newItem) => {
							result.push({ _id: uuidv4(), status: newItem.name, ...newItem });
						});
						oldDataItem.statuses = result;
					}
				}

				return updateDataCopy;
			});
		}
	);

	useEffect(() => {
		if (!locationId) {
			return;
		}

		addSubscription(
			["updateReynoldsInventoryMappingStatuses", locationId],
			[
				{
					context: "onUpdateReynoldsInventoryMapping",
					query: ON_UPDATE_REYNOLDS_INVENTORY_MAPPING,
					variables: {
						locationId,
					},
				},
			]
		);
	}, [addSubscription, location]);

	if (!baseDataList || !data?.length) {
		return null;
	}

	return (
		<Stack width={1} sx={{ minWidth: 100 }} className="my-[32px]">
			<Typography className="!font-montserrat !text-default !text-[20px] !font-medium">
				{isLoading ? <Skeleton height="50px" width="250px" /> : "Publish Inventory"}
			</Typography>

			{isLoading ? (
				<Loading />
			) : (
				<Grid
					container
					flexDirection={"row"}
					justifyContent={"flex-start"}
					spacing={2}
					sx={{ border: "none" }}
					className="!mt-[0px]"
				>
					{data?.map((section, index) => (
						<Grid item key={index} xs={6} sx={{ minWidth: 480 }}>
							<InventoryMappingTable
								title={section.title}
								statuses={section.statuses}
								dmsMappingOptions={baseDataList.mappingOptions}
							/>
						</Grid>
					))}
				</Grid>
			)}
		</Stack>
	);
}

/********************************************* HELPER COMPONENTS ************************************************/
function Loading() {
	return (
		<Grid container flexDirection="row" sx={{ marginBottom: "20px" }}>
			<LoadingGridItem />
			<LoadingGridItem marginLeft="50px" />
		</Grid>
	);
}

function LoadingGridItem({ marginLeft }: { marginLeft?: string | number }) {
	return (
		<Grid item sx={{ width: "480px", marginLeft }}>
			<Skeleton height="50px" width="450px" />
			<TableLoader toolbar buttons={2} content rows={5} multiTitles={2} />
		</Grid>
	);
}
